import { Link, Navigate, useNavigate } from "react-router-dom";
import AuthLayout from "../../app-layouts/AuthLayout";
import { GoogleIcon, LinkedinIcon } from "../../assets/custom-icons";
import { inputStyle } from "../../app-util/helpers";
import { Checkbox } from "../../app-components/CheckboxComponent";
import { FormEvent, useState } from "react";
import { loginSchema, loginType } from "../../app-schemas/auth.schema";
import apiClient from "../../api-services/ApiClient";
import { ELITELINKS, SITELINKS } from "../../app-routes/links";
import {
  GOOGLE_URL,
  GOOGLE_URL_APP,
  LINKEDIN_URL,
  storeToken,
  storeUser,
} from "../../api-services/process";
import PasswordToggle from "../../app-components/PasswordToggle";
import { successAlert } from "../../app-util/SweetAlert";
import Preloader from "../../app-components/preloader/Preloader";
import useAuth from "../../custom-hooks/useAuth";
import { HoneConnectLogo } from "../../assets/img";
import { Capacitor } from "@capacitor/core";

const Login = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: preloading } = useAuth();

  // component-state
  const [serverError, setServerError] = useState<string>("");
  const [pageLoading, setPageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState<loginType>({
    userID: "",
    password: "",
    isRemember: false,
  });

  // use-state-form-errors
  const [errors, setErrors] = useState<
    Partial<Record<keyof loginType, string>>
  >({});

  // handle-input-change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // handle-checkbox-change
  const handleCheckBox = (name: string, status?: boolean) => {
    setLoginData((prevState) => ({
      ...prevState,
      [name]: status,
    }));
  };

  // handle-login-submit
  const handleEmailLogin = async (e: FormEvent) => {
    e.preventDefault();
    setErrors({});
    setServerError("");

    const result = loginSchema.safeParse(loginData);
    if (result.success) {
      try {
        setLoading(true);
        const res = await apiClient.post("login", result.data);
        const user = res.data?.data?.user;

        successAlert();
        storeToken(res.data?.data?.token);
        storeUser(user);
        if (!user?.username || !user?.DOB || !user?.country_id) {
          navigate(SITELINKS.REGISTER_STEP_TWO);
        } else {
          navigate(SITELINKS.HOME);
        }
      } catch (error: any) {
        setServerError(JSON.stringify(error));
      } finally {
        setLoading(false);
      }
    } else {
      const formattedErrors: Partial<Record<keyof loginType, string>> = {};

      result.error.errors.forEach((err) => {
        if (err.path.length > 0) {
          const key = err.path[0] as keyof loginType;
          formattedErrors[key] = err.message;
        }
      });

      setErrors(formattedErrors);
    }
  };

  // handle-google-login
  const handleGoogleLogin = () => {
    setPageLoading(true);

    const isNative = Capacitor.isNativePlatform();

    if (isNative) window.location.href = GOOGLE_URL_APP;
    else window.location.href = GOOGLE_URL;
  };

  // handle-linkedin-login
  const handleLinkedInLogin = () => {
    setPageLoading(true);
    window.location.href = LINKEDIN_URL;
  };

  // Show preloader while loading
  if (preloading) {
    return (
      <div>
        <Preloader loading={preloading} />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={ELITELINKS.ELITE} replace />;
  }

  return (
    <AuthLayout>
      <div className="lg:col-span-3 bg-white shadow relative h-[100vh]">
        <div className="bg-white w-full">
          <div className="text-sm font-semibold flex justify-center px-4 pt-20">
            <Link to={SITELINKS.LOGIN} className="text-honeConnectPurple flex">
              <img src={HoneConnectLogo} alt="tiny top" className="h-[35px]" />
            </Link>
          </div>

          <div className="pt-10 lg:pt-20 pb-0 px-2 lg:px-10 lg:px-28 text-honeConnectGray px-5 pt-6 pb-5">
            <div>
              <h4 className="text-[2.5rem] text-honeConnectBlack font-medium mb-2.5 text-honeConnectPurple leading-[35px] text-center">
                Sign in
              </h4>
            </div>
          </div>
        </div>

        {/* react-login-form */}
        <div className="relative top-[-10px] w-full pt-4 lg:pt-20 pb-0 px-5 lg:px-10 lg:px-28 text- bg-white rounded-t-lg text-gray-100">
          {/* social media connect */}
          <div className="flex gap-5">
            <div className="flex flex-col text-center w-1/2">
              <button
                type="button"
                onClick={handleLinkedInLogin}
                className="flex items-center bg-transparent text-gray-500 w-full py-2 px-3 rounded-xl border-1 border border-solid border-gray-200 text-sm  shadow-sm hover:shadow"
              >
                <img
                  src={LinkedinIcon}
                  alt="Google Icon"
                  className="w-[30px] h-[25px]"
                />
                <div className="w-3/4 font-[500] font-medium">
                  <span> LinkedIn</span>
                </div>
              </button>
            </div>

            <div className="flex flex-col text-center w-1/2">
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="flex items-center bg-transparent text-gray-500 w-full py-2 px-3 rounded-xl border-1 border border-solid border-gray-200 text-sm  shadow-sm hover:shadow"
              >
                <img
                  src={GoogleIcon}
                  alt="Google Icon"
                  className="w-[25px] h-[25px]"
                />
                <div className="w-3/4 font-[500] font-medium">
                  <span> Google</span>
                </div>
              </button>
            </div>
          </div>

          <div className="flex justify-center text-gray-500 text-sm py-6 pb-4">
            <p>— or sign in with —</p>
          </div>

          <form
            onSubmit={handleEmailLogin}
            autoComplete="off"
            autoCorrect="off"
          >
            <div className="flex flex-col text-sm font-medium mb-4  text-gray-500">
              <label htmlFor="userID" className="mb-0.5 text-[12px]">
                Email / Username
              </label>
              <input
                onChange={handleInputChange}
                className={`${inputStyle} bg-transparent border-gray-200 text- rounded-[0px] outline-none focus:outline-none font-medium text-[12px] shadow-sm hover:shadow`}
                placeholder="Enter your email or username"
                name="userID"
                autoComplete="off"
              />
              <span className="text-red-600 text-[10px]">{errors.userID}</span>
            </div>

            <div className="flex flex-col text-sm font-medium mb-4 relative text-gray-500">
              <label htmlFor="Password" className="mb-0.5 text-[12px]">
                Password
              </label>
              <input
                type="password"
                name="password"
                id="password_input"
                onChange={handleInputChange}
                className={`${inputStyle} bg-transparent border-gray-200 text- rounded-[0px] outline-none focus:outline-none font-medium text-[12px] shadow-sm hover:shadow`}
                placeholder="Enter your password"
                autoComplete="off"
              />

              <div className="absolute top-[2.1rem] right-3">
                <PasswordToggle targetId="password_input" />
              </div>
              <span className="text-red-600 text-[10px]">
                {errors.password}
              </span>
            </div>

            <div className="flex items-center justify-between text-xs text-honeConnectGray font-medium mt-6 mb-4  text-gray-100">
              <div className="flex items-center gap-1 text-gray-500">
                <Checkbox
                  label="Remember Me"
                  checked={loginData.isRemember}
                  onChange={() => {
                    handleCheckBox("isRemember", !loginData.isRemember);
                  }}
                />
              </div>

              <Link to={SITELINKS.FORGOT_PASSWORD} className="text-gray-500">
                Forgot Password?
              </Link>
            </div>

            <p className="text-red-600 text-[12px] text-center mb-2">
              {serverError}
            </p>

            <button
              type="submit"
              className="disabled:bg-gray-600 bg-honeConnectPurple text-[#f2f2f2] text-sm w-full p-3 py-4 border-0 mt-2 hover:shadow-sm rounded-lg border-[1px] border-[#8a42b6]  shadow-sm hover:shadow"
              disabled={loading}
            >
              Sign In
            </button>
          </form>

          <div className="text-center mt-8 text-sm text-gray-500">
            Don't have an account?{" "}
            <Link
              to={SITELINKS.REGISTER}
              className="text-honeConnectOrange font-semibold underline"
            >
              Sign Up
            </Link>
          </div>
        </div>
      </div>

      <Preloader loading={pageLoading} />
    </AuthLayout>
  );
};

export default Login;
